import { useState, useEffect, useCallback } from "react";
import QRCode from "qrcode";
import QueuePoster from "./QueuePoster";

/**
 * QueueTab — reusable owner queue management tab
 *
 * Props:
 *   businessId   string   required
 *   businessName string   required
 *   apiBase      string   API Gateway base URL
 *   appDomain    string   e.g. "qkard.app" or "lucknowmarkets.com"
 *   accentColor  string   brand color e.g. "#6c63ff" or "#FF6B35"
 *   brandName    string   e.g. "QKARD" or "Lucknow Markets"
 *
 * QKARD usage (OwnerDashboard.jsx):
 *   <QueueTab businessId={businessId} businessName={businessName}
 *     apiBase="https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod"
 *     appDomain="qkard.app" accentColor="#6c63ff" brandName="QKARD" />
 *
 * LucknowMarkets usage (OwnerDashboard or ShopDashboard):
 *   <QueueTab businessId={shopId} businessName={shopName}
 *     apiBase="https://ge177tmu0d.execute-api.ap-south-1.amazonaws.com/prod"
 *     appDomain="lucknowmarkets.com" accentColor="#FF6B35" brandName="Lucknow Markets" />
 */
export default function QueueTab({
  businessId,
  businessName,
  apiBase = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod",
  appDomain = "qkard.app",
  accentColor = "#6c63ff",
  brandName = "QKARD",
}) {
  const [queue, setQueue] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [calling, setCalling] = useState(false);
  const [queueLabel, setQueueLabel] = useState("Main Queue");
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [showPoster, setShowPoster] = useState(false);

  const waiting = tokens.filter(t => t.status === "waiting");
  const current = tokens.find(t => t.status === "calling" || t.status === "serving");
  const done = tokens.filter(t => t.status === "done" || t.status === "billed");
  const queueUrl = queue ? `https://${appDomain}/q/${queue.queueId}` : "";

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchQueue = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/queue/list?businessId=${businessId}`);
      if (!res.ok) { setLoading(false); return; }
      const data = await res.json();
      const active = (data.queues || []).find(q => q.status === "active");
      if (active) {
        setQueue(active);
        const r2 = await fetch(`${apiBase}/queue/${active.queueId}`);
        const d2 = await r2.json();
        setTokens(d2.tokens || []);
      } else {
        setQueue(null);
        setTokens([]);
      }
    } catch {}
    setLoading(false);
  }, [businessId, apiBase]);

  useEffect(() => {
    fetchQueue();
    const iv = setInterval(fetchQueue, 6000);
    return () => clearInterval(iv);
  }, [fetchQueue]);

  // ── QR — branded color ────────────────────────────────────────────────────
  useEffect(() => {
    if (!queue?.queueId) return;
    QRCode.toDataURL(queueUrl, {
      width: 300, margin: 2,
      color: { dark: accentColor, light: "#ffffff" },
      errorCorrectionLevel: "H",
    }).then(setQrDataUrl).catch(() => {});
  }, [queue?.queueId, queueUrl, accentColor]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const createQueue = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${apiBase}/queue/create`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessId, businessName, queueLabel }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      await fetchQueue();
    } catch (e) { alert("Could not create queue: " + e.message); }
    finally { setCreating(false); }
  };

  const callNext = async () => {
    if (!queue?.queueId || waiting.length === 0) return;
    setCalling(true);
    try {
      await fetch(`${apiBase}/queue/call`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ queueId: queue.queueId }),
      });
      await fetchQueue();
    } catch {}
    setCalling(false);
  };

  const markDone = async (tokenId) => {
    try {
      await fetch(`${apiBase}/queue/done`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ queueId: queue.queueId, tokenId }),
      });
      await fetchQueue();
    } catch {}
  };

  const togglePause = async () => {
    try {
      await fetch(`${apiBase}/queue/pause`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ queueId: queue.queueId, pause: !queue.isPaused }),
      });
      await fetchQueue();
    } catch {}
  };

  const resetQueue = async () => {
    if (!window.confirm("Reset queue? All tokens will be cleared.")) return;
    try {
      await fetch(`${apiBase}/queue/reset`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ queueId: queue.queueId }),
      });
      await fetchQueue();
    } catch {}
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading queue…</div>
  );

  if (!queue) return (
    <div style={s.setupWrap}>
      <div style={s.setupIcon}>🎫</div>
      <h2 style={s.setupTitle}>Queue System</h2>
      <p style={s.setupSub}>Let customers join your queue from their phone — no app needed</p>
      <input style={s.labelInput} value={queueLabel} maxLength={40}
        onChange={e => setQueueLabel(e.target.value)}
        placeholder="Queue name (e.g. Main Counter)" />
      <button style={{ ...s.startBtn, background: accentColor }}
        onClick={createQueue} disabled={creating}>
        {creating ? "Starting…" : "▶ Start Queue"}
      </button>
    </div>
  );

  const canCall = waiting.length > 0 && !calling && !queue.isPaused;

  return (
    <div style={s.root}>

      {/* Status bar */}
      <div style={s.statusBar}>
        <span style={{ ...s.dot, background: queue.isPaused ? "#ffd600" : "#22c55e" }} />
        <span style={s.statusText}>{queue.isPaused ? "Paused" : "Live"}</span>
        <span style={s.statusLabel}>{queue.queueLabel || "Queue"}</span>
        <div style={s.statusActions}>
          <button style={s.smBtn} onClick={togglePause}>
            {queue.isPaused ? "▶ Resume" : "⏸ Pause"}
          </button>
          <button style={{ ...s.smBtn, color: "#ef4444", borderColor: "#ef4444" }} onClick={resetQueue}>
            ↺ Reset
          </button>
        </div>
      </div>

      {/* Counters */}
      <div style={s.counters}>
        <div style={s.counterCard}>
          <span style={s.counterLabel}>WAITING</span>
          <span style={{ ...s.counterNum, color: accentColor }}>{waiting.length}</span>
        </div>
        <div style={s.counterCard}>
          <span style={s.counterLabel}>SERVED TODAY</span>
          <span style={{ ...s.counterNum, color: "#22c55e" }}>{done.length}</span>
        </div>
      </div>

      {/* Now serving */}
      {current && (
        <div style={s.currentCard}>
          <div style={{ ...s.currentLabel, color: "#22c55e" }}>NOW SERVING</div>
          <div style={s.currentNum}>#{current.tokenNumber}</div>
          <div style={s.currentName}>{current.customerName || "Guest"}</div>
          {current.items?.length > 0 && (
            <div style={{ marginBottom: 12, background: "rgba(255,255,255,0.05)", borderRadius: 8, padding: "8px 12px", textAlign: "left" }}>
              {current.items.map(it => (
                <div key={it.itemId} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#ccc", padding: "2px 0" }}>
                  <span>{it.name} x{it.qty}</span><span>Rs.{it.price * it.qty}</span>
                </div>
              ))}
              {current.total > 0 && (
                <div style={{ borderTop: "1px solid #333", marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between", fontWeight: 700, color: "#fff", fontSize: 14 }}>
                  <span>Total</span><span>Rs.{current.total}</span>
                </div>
              )}
            </div>
          )}
          <button style={s.doneBtn} onClick={() => markDone(current.tokenId)}>Done</button>
        </div>
      )}

      {/* NEXT button */}
      <button
        style={{
          ...s.nextBtn,
          background: `linear-gradient(135deg, ${accentColor} 0%, #4facfe 100%)`,
          opacity: canCall ? 1 : 0.4,
          cursor: canCall ? "pointer" : "not-allowed",
        }}
        onClick={callNext}
        disabled={!canCall}
      >
        {calling ? "…" : "NEXT"}
        <span style={s.nextSub}>
          {calling ? "" : waiting.length > 0
            ? `#${waiting[0].tokenNumber} · ${waiting[0].items?.length > 0 ? waiting[0].items.map(i => `${i.name} x${i.qty}`).join(", ") : waiting[0].customerName || "Guest"}`
            : "No one waiting"}
        </span>
      </button>

      {/* Waiting list */}
      {waiting.length > 0 && (
        <div style={s.listSection}>
          <div style={s.listTitle}>WAITING ({waiting.length})</div>
          {waiting.map((t, i) => (
            <div key={t.tokenId} style={s.listItem}>
              <span style={{ ...s.listNum, color: accentColor }}>#{t.tokenNumber}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, color: "#ccc", marginBottom: 2 }}>
                  {t.items?.length > 0
                    ? t.items.map(it => `${it.name} x${it.qty}`).join(", ")
                    : t.customerName || "Guest"}
                </div>
                {t.total > 0 && <div style={{ fontSize: 13, color: "#888" }}>Rs.{t.total}</div>}
              </div>
              <span style={s.listPos}>#{i + 1} in line</span>
            </div>
          ))}
        </div>
      )}

      {/* Poster + QR */}
      <div style={s.qrSection}>
        <button
          style={{ ...s.posterBtn, background: `linear-gradient(135deg, ${accentColor} 0%, #4facfe 100%)` }}
          onClick={() => setShowPoster(true)}
        >
          🎨 View & Print Queue Poster
        </button>
        <button style={s.qrToggle} onClick={() => setShowQr(v => !v)}>
          {showQr ? "▲ Hide raw QR" : "▼ Show raw QR"}
        </button>
        {showQr && qrDataUrl && (
          <div style={s.qrBox}>
            <p style={s.qrHint}>Customers scan this to join remotely</p>
            <img src={qrDataUrl} alt="Queue QR" style={s.qrImg} />
            <div style={s.qrUrl}>{appDomain}/q/{queue.queueId}</div>
          </div>
        )}
      </div>

      {showPoster && (
        <QueuePoster
          queueId={queue.queueId}
          businessName={businessName}
          queueLabel={queue.queueLabel}
          appDomain={appDomain}
          accentColor={accentColor}
          brandName={brandName}
          onClose={() => setShowPoster(false)}
        />
      )}
    </div>
  );
}

const s = {
  root: { padding: 16, maxWidth: 480, margin: "0 auto" },
  setupWrap: { padding: "48px 24px", textAlign: "center", maxWidth: 400, margin: "0 auto" },
  setupIcon: { fontSize: 56, marginBottom: 12 },
  setupTitle: { fontSize: 22, fontWeight: 700, margin: "0 0 8px" },
  setupSub: { fontSize: 14, color: "#888", margin: "0 0 24px", lineHeight: 1.5 },
  labelInput: {
    width: "100%", boxSizing: "border-box", padding: "12px 16px",
    borderRadius: 10, border: "1px solid #333", background: "#111",
    color: "#fff", fontSize: 15, marginBottom: 14, outline: "none",
  },
  startBtn: {
    width: "100%", color: "#fff", border: "none",
    borderRadius: 12, padding: 16, fontSize: 17, fontWeight: 700, cursor: "pointer",
  },
  statusBar: {
    display: "flex", alignItems: "center", gap: 8,
    background: "#1a1a1a", border: "1px solid #2a2a2a",
    borderRadius: 12, padding: "10px 14px", marginBottom: 12,
  },
  dot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  statusText: { fontSize: 13, fontWeight: 700, color: "#fff" },
  statusLabel: { fontSize: 13, color: "#888", flex: 1 },
  statusActions: { display: "flex", gap: 8 },
  smBtn: {
    background: "transparent", border: "1px solid #444", color: "#aaa",
    borderRadius: 8, padding: "4px 10px", fontSize: 12, cursor: "pointer", fontWeight: 600,
  },
  counters: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 },
  counterCard: {
    background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 12,
    padding: "16px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
  },
  counterLabel: { fontSize: 10, letterSpacing: 1.5, color: "#555", fontWeight: 700 },
  counterNum: { fontSize: 40, fontWeight: 900, fontFamily: "'Courier New', monospace", lineHeight: 1 },
  currentCard: {
    background: "#0d2a0d", border: "2px solid #22c55e",
    borderRadius: 14, padding: 20, textAlign: "center", marginBottom: 14,
  },
  currentLabel: { fontSize: 11, letterSpacing: 2, fontWeight: 700, marginBottom: 6 },
  currentNum: { fontSize: 48, fontWeight: 900, fontFamily: "'Courier New', monospace", color: "#fff", lineHeight: 1 },
  currentName: { fontSize: 16, color: "#aaa", marginTop: 6, marginBottom: 14 },
  doneBtn: {
    background: "#22c55e", color: "#000", border: "none",
    borderRadius: 10, padding: "10px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer",
  },
  nextBtn: {
    width: "100%", color: "#fff", border: "none", borderRadius: 20,
    padding: "28px 20px", fontSize: 48, fontWeight: 900, letterSpacing: 4,
    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
    marginBottom: 20, lineHeight: 1, boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  },
  nextSub: { fontSize: 14, fontWeight: 500, letterSpacing: 0, opacity: 0.8 },
  listSection: { marginBottom: 20 },
  listTitle: { fontSize: 11, letterSpacing: 1.5, color: "#555", fontWeight: 700, marginBottom: 8 },
  listItem: {
    display: "flex", alignItems: "center", gap: 12,
    background: "#1a1a1a", border: "1px solid #2a2a2a",
    borderRadius: 10, padding: "10px 14px", marginBottom: 6,
  },
  listNum: { fontSize: 18, fontWeight: 800, fontFamily: "'Courier New', monospace", minWidth: 48 },
  listName: { flex: 1, fontSize: 14, color: "#ccc" },
  listPos: { fontSize: 12, color: "#555" },
  qrSection: { display: "flex", flexDirection: "column", gap: 10 },
  posterBtn: {
    width: "100%", color: "#fff", border: "none",
    borderRadius: 12, padding: "14px 20px", fontSize: 15, fontWeight: 700, cursor: "pointer",
  },
  qrToggle: {
    background: "transparent", border: "1px solid #333",
    color: "#888", borderRadius: 10, padding: "10px 20px", fontSize: 14, cursor: "pointer",
  },
  qrBox: {
    background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 14,
    padding: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
  },
  qrHint: { fontSize: 13, color: "#888", margin: 0 },
  qrImg: { width: 180, height: 180, borderRadius: 8 },
  qrUrl: { fontSize: 12, color: "#555", fontFamily: "monospace" },
};
